import { CirclePause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pauseSummary, waitingText } from "@/lib/agent-pause";
import type { AgentWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";

const TINT = "var(--prio-high)";

/**
 * The paused agent's banner above the composer: why it is paused, when a limit pause
 * lifts by itself, what is waiting, and Resume — the one way on, which used to be
 * buried in the ⋯ menu. Without `onResume` (anyone below admin) it is the text alone.
 */
export function AgentPausedBanner({
  agent,
  queued,
  now,
  busy = false,
  onResume,
}: {
  agent: AgentWithContext;
  queued: number;
  now: number;
  busy?: boolean;
  onResume?: () => void;
}) {
  const summary = pauseSummary(agent, now);
  if (!summary) return null;
  return (
    <div
      role="region"
      aria-label="Agent paused"
      data-agent-paused={agent.pausedReason ?? "user"}
      className="border-t"
      style={{ borderColor: `color-mix(in oklab, ${TINT} 40%, transparent)`, backgroundColor: `color-mix(in oklab, ${TINT} 8%, transparent)` }}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-2.5 px-4 py-2 win-md:px-6">
        <CirclePause className="size-4 shrink-0" style={{ color: TINT }} aria-hidden />
        <div className="min-w-0 flex-1 text-[12.5px] leading-snug">
          <p className="font-medium" style={{ color: TINT }}>
            {summary.title}
          </p>
          <p className="text-[11.5px] text-muted-foreground">
            {waitingText(queued)}
            {/* On a phone the title says enough; the chat line above has the rest. */}
            {summary.detail ? <span className="hidden win-sm:inline"> · {summary.detail}</span> : null}
          </p>
        </div>
        {onResume ? (
          <Button size="sm" className="shrink-0" loading={busy} onClick={onResume}>
            <Play /> {summary.action}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** "paused · 12:30" on a list row: a paused agent and, for a limit, when it lifts. */
export function AgentPausePill({ agent, now, className }: { agent: AgentWithContext; now: number; className?: string }) {
  const summary = pauseSummary(agent, now);
  if (!summary) return null;
  return (
    <span
      title={summary.detail ? `${summary.title}. ${summary.detail}` : summary.title}
      className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-medium leading-4", className)}
      style={{ color: TINT, backgroundColor: `color-mix(in oklab, ${TINT} 16%, transparent)` }}
    >
      <CirclePause className="size-3" aria-hidden />
      {summary.pill}
    </span>
  );
}
